/** Client portal: the tax planning questionnaire, and the firm's approved tax plan to read and accept. */
import { formatCents, roundHalfAwayFromZero, type PlanningQuestion } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Printer, Sparkles } from "lucide-react";
import { useState } from "react";
import { Empty, Loading, PageHeader, Section } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const usd = (n: number) => formatCents(roundHalfAwayFromZero(n) * 100).replace(/\.00$/, "");
const usdCents = (n: number) => formatCents(roundHalfAwayFromZero(n * 100)).replace(/\.00$/, "");
const STATE_NAMES: Record<string, string> = { MD: "Maryland", VA: "Virginia", DC: "DC" };

type Snapshot = {
  firmName: string;
  clientName: string;
  title: string;
  summary: string;
  baseYear: number;
  years: number;
  recommendedScenarioId: string;
  reviewedBy: string;
  reviewedAt: string;
  estimatedYears: number[];
  /** The plan's state (copies approved before October 2026 don't have it). */
  stateCode?: "MD" | "VA" | "DC" | "flat" | "none";
  limitations: string[];
  /** Current-law notes on the state figures (copies approved before 28 October 2026 don't have them). */
  stateNotes?: string[];
  scenarios: Array<{
    id: string;
    name: string;
    position: number;
    total: number;
    savings: number;
    strategies: Array<{ key: string; title: string; summary: string; actionItem: string; owner: "client" | "firm"; years: number[] }>;
    years: Array<{
      year: number;
      federal: number;
      state: number;
      total: number;
      marginalRate: number;
      effectiveRate: number;
      notes: string[];
      /** Federal installments: the first three, and the last (which takes the remainder). */
      estimatesPerQuarter: number;
      estimatesLastQuarter?: number;
      stateEstimatesPerQuarter?: number | null;
      estimated: boolean;
      stateEstimated?: boolean;
    }>;
  }>;
};
type PortalPlan = {
  id: string;
  clientId: string;
  title: string;
  status: "preparing" | "shared" | "accepted";
  plan: Snapshot | null;
  questionnaire: { answers: Record<string, unknown> } | null;
  acceptedAt: string | null;
  acceptedName: string | null;
  actionItems: Array<{ id: string; title: string; owner: "client" | "firm"; dueOn: string | null; status: string }>;
};

function Questionnaire({ plan, questions }: { plan: PortalPlan; questions: PlanningQuestion[] }) {
  const [answers, setAnswers] = useState<Record<string, unknown>>(plan.questionnaire?.answers ?? {});
  // Number answers are kept as typed ("1." on the way to "1.5") and checked when saved.
  const [bad, setBad] = useState<string[]>([]);
  const parsed = () => {
    const out: Record<string, unknown> = {};
    const wrong: string[] = [];
    for (const q of questions) {
      const v = answers[q.key];
      if (q.kind === "text" || v === undefined || v === null || typeof v === "number") out[q.key] = v;
      else {
        const raw = String(v).replace(/[$,\s]/g, "");
        if (raw === "") out[q.key] = null;
        else if (Number.isFinite(Number(raw))) out[q.key] = Number(raw);
        else wrong.push(q.key);
      }
    }
    setBad(wrong);
    return wrong.length ? null : out;
  };
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: (submit: boolean) => {
      const a = parsed();
      if (!a) return Promise.reject(new Error("Enter numbers only in the highlighted answers"));
      return api.send("POST", `/api/portal/tax-plans/${plan.id}/questionnaire`, { answers: a, submit });
    },
  });
  const set = (k: string, v: unknown) => setAnswers({ ...answers, [k]: v });
  return (
    <Section title="A few questions for your tax plan" description="Your best estimates are fine. Leave anything blank that doesn't apply.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(true);
        }}
      >
        {questions.map((q) => (
          <div key={q.key} className="space-y-1">
            <Label htmlFor={`pq-${q.key}`}>{q.label}</Label>
            {q.help && <p className="text-xs text-muted-foreground">{q.help}</p>}
            {q.kind === "text" ? (
              <Textarea id={`pq-${q.key}`} rows={2} value={String(answers[q.key] ?? "")} onChange={(e) => set(q.key, e.target.value)} />
            ) : (
              <Input
                id={`pq-${q.key}`}
                inputMode="decimal"
                className={bad.includes(q.key) ? "border-destructive sm:w-60" : "sm:w-60"}
                placeholder={q.kind === "count" ? "0" : "$0"}
                aria-invalid={bad.includes(q.key) || undefined}
                value={answers[q.key] === undefined || answers[q.key] === null ? "" : String(answers[q.key])}
                onChange={(e) => set(q.key, e.target.value)}
              />
            )}
          </div>
        ))}
        {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={save.isPending}>
            Send to my accountant
          </Button>
          <Button type="button" variant="outline" onClick={() => save.mutate(false)} disabled={save.isPending}>
            Save and finish later
          </Button>
          {save.isSuccess && !save.variables && <span className="self-center text-sm text-muted-foreground">Saved</span>}
        </div>
      </form>
    </Section>
  );
}

type SnapshotYear = Snapshot["scenarios"][number]["years"][number];

/**
 * The year's estimated payments: the federal figure (the same as the firm's plan screen and the
 * set-aside), and the state's when the plan has the state withholding to work it out from.
 */
function EstimateSentence({ y, stateCode }: { y: SnapshotYear; stateCode: Snapshot["stateCode"] }) {
  const fed = y.estimatesPerQuarter;
  const last = y.estimatesLastQuarter;
  const name = stateCode ? STATE_NAMES[stateCode] : undefined;
  const stateAmount = y.stateEstimatesPerQuarter ?? null;
  // What to say about the state: a figure, a promise to schedule it, or nothing (no state income tax).
  const state =
    stateCode === undefined
      ? { amount: null, text: "any state estimates" }
      : stateCode === "flat"
        ? { amount: null, text: "your state's estimates" }
        : name
          ? { amount: stateAmount, text: `${name} estimates` }
          : null;
  if (fed <= 0 && (!state || (state.amount !== null && state.amount <= 0))) return null;
  return (
    <p className="mt-3 text-sm">
      {fed > 0 ? (
        <>
          Federal estimated tax for {y.year}: about <span className="font-semibold tabular">{usd(fed)}</span> each quarter on top of your withholding
          {last !== undefined && last !== fed && <> (the last payment {usdCents(last)})</>}
        </>
      ) : (
        <>No federal estimated tax is needed for {y.year} beyond your withholding</>
      )}
      {state && state.amount !== null && state.amount > 0 && (
        <>
          , {fed > 0 ? "plus" : "but"} about <span className="font-semibold tabular">{usd(state.amount)}</span> each quarter of {name} estimated tax
        </>
      )}
      {state && state.amount === null && <>, {fed > 0 ? "plus" : "and"} {state.text}, which we'll schedule</>}. We'll send you the exact amounts and dates.
    </p>
  );
}

function PlanDocument({ p }: { p: PortalPlan }) {
  const s = p.plan!;
  // Years whose Maryland standard deduction is estimated (indexed until the Comptroller publishes it).
  const stateEstimatedYears = s.stateCode === "MD" ? [...new Set(s.scenarios.flatMap((sc) => sc.years.filter((y) => y.stateEstimated).map((y) => y.year)))].sort() : [];
  const rec = s.scenarios.find((x) => x.id === s.recommendedScenarioId)!;
  const current = s.scenarios.find((x) => x.position === 0)!;
  const [name, setName] = useState("");
  const accept = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/portal/tax-plans/${p.id}/accept`, { name }) });
  const range = s.years > 1 ? `${s.baseYear}–${s.baseYear + s.years - 1}` : String(s.baseYear);
  return (
    <article className="overflow-hidden rounded-xl border border-card-border bg-card shadow-[var(--card-shadow)] print:border-0 print:shadow-none">
      <header className="bg-primary px-6 py-6 text-primary-foreground sm:px-8">
        <p className="text-xs font-medium uppercase tracking-widest opacity-80">{s.firmName}</p>
        <h2 className="mt-1 text-2xl font-semibold">{s.title}</h2>
        <p className="mt-1 text-sm opacity-90">
          Prepared for {s.clientName} · {range}
        </p>
      </header>
      <div className="space-y-8 px-6 py-6 sm:px-8">
        <section>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border p-4">
              <p className="text-xs text-muted-foreground">Your taxes on the current path</p>
              <p className="text-xl font-semibold tabular">{usd(current.total)}</p>
            </div>
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
              <p className="text-xs text-muted-foreground">With our recommendations</p>
              <p className="text-xl font-semibold tabular">{usd(rec.total)}</p>
            </div>
            <div className={cn("rounded-lg border p-4", rec.savings >= 0 ? "border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40" : "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40")}>
              <p className="text-xs text-muted-foreground">{rec.savings >= 0 ? `Projected savings over ${s.years} year${s.years === 1 ? "" : "s"}` : "More tax now, in exchange for later savings"}</p>
              <p className="text-xl font-semibold tabular">{usd(Math.abs(rec.savings))}</p>
            </div>
          </div>
          {s.summary && <p className="mt-5 whitespace-pre-wrap text-[15px] leading-relaxed">{s.summary}</p>}
        </section>

        <section>
          <h3 className="mb-3 flex items-center gap-2 text-base font-semibold">
            <Sparkles className="h-4 w-4 text-primary" /> What we recommend
          </h3>
          <ol className="space-y-3">
            {rec.strategies.map((st, i) => (
              <li key={i} className="rounded-lg border border-border p-4">
                <p className="font-medium">
                  {i + 1}. {st.title}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{st.summary}</p>
                <p className="mt-2 text-sm">
                  <span className="font-medium">{st.owner === "client" ? "Your step" : "We'll handle"}:</span> {st.actionItem} ({st.years.join(", ")}).
                </p>
              </li>
            ))}
          </ol>
        </section>

        <section>
          <h3 className="mb-3 text-base font-semibold">Year by year</h3>
          <TableScroll label="Year by year table">
            <table className="w-full min-w-[420px] text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-left font-medium">Total tax</th>
                  {current.years.map((y) => (
                    <th key={y.year} className="py-2 pl-3 text-right font-medium tabular">
                      {y.year}
                      {(y.estimated || y.stateEstimated) && "*"}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {s.scenarios.map((sc) => (
                  <tr key={sc.id} className={cn("border-b border-border/60", sc.id === rec.id && "bg-primary/5 font-semibold")}>
                    <td className="py-2 pr-3">
                      {sc.name}
                      {sc.id === rec.id && <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">Recommended</span>}
                    </td>
                    {sc.years.map((y) => (
                      <td key={y.year} className="py-2 pl-3 text-right tabular">
                        {usd(y.total)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          {rec.years[0] && <EstimateSentence y={rec.years[0]} stateCode={s.stateCode} />}
        </section>

        <section className="text-xs text-muted-foreground">
          <p>
            Reviewed by {s.reviewedBy} on {formatDate(s.reviewedAt.slice(0, 10))}. Federal and state income tax, projected from the figures we have today; actual results depend on your final income and the law in each year.
            {s.estimatedYears.length > 0 && ` * ${s.estimatedYears.join(", ")} use estimated tax tables (the IRS publishes each year's figures the fall before).`}
            {stateEstimatedYears.length > 0 && ` ${s.estimatedYears.length ? "" : "* "}${stateEstimatedYears.join(", ")}: Maryland's standard deduction is estimated until the Comptroller publishes it.`}
          </p>
          <ul className="mt-2 list-disc space-y-0.5 pl-4">
            {(s.stateNotes ?? []).map((n) => (
              <li key={n}>{n}</li>
            ))}
            {s.limitations.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </section>

        {p.status === "accepted" ? (
          <section className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/40">
            <p className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Accepted by {p.acceptedName} on {formatDate(p.acceptedAt!.slice(0, 10))}
            </p>
            {p.actionItems.length > 0 && (
              <ul className="mt-3 space-y-1.5 text-sm">
                {p.actionItems.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center gap-2">
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", a.owner === "client" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>{a.owner === "client" ? "You" : "Us"}</span>
                    <span className={cn(a.status === "done" && "line-through")}>{a.title}</span>
                    {a.dueOn && <span className="text-xs text-muted-foreground">by {formatDate(a.dueOn)}</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : (
          <form
            className="rounded-lg border border-primary/30 bg-primary/5 p-4 print:hidden"
            onSubmit={(e) => {
              e.preventDefault();
              accept.mutate();
            }}
          >
            <p className="font-medium">Accept this plan</p>
            <p className="mt-1 text-sm text-muted-foreground">Type your full name to accept. Each recommendation becomes a step on your to-do list, and we'll follow up on ours.</p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Input aria-label="Your full name" placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
              <Button type="submit" disabled={accept.isPending || name.trim().length < 2}>
                Accept the plan
              </Button>
            </div>
            {accept.error && <p className="mt-2 text-sm text-destructive">{errorMessage(accept.error)}</p>}
          </form>
        )}
      </div>
    </article>
  );
}

export function PortalTaxPlanPage() {
  const { data, isLoading } = useQuery<{ plans: PortalPlan[]; questions: PlanningQuestion[] }>({ queryKey: ["/api/portal/tax-plans"] });
  return (
    <>
      <PageHeader
        title="Tax Plan"
        description="Your tax plan from your accountant: what to expect, what we recommend, and your next steps."
        actions={
          data?.plans.some((p) => p.plan) && (
            <Button variant="outline" onClick={() => window.print()} className="print:hidden">
              <Printer className="h-4 w-4" /> Print or save as PDF
            </Button>
          )
        }
      />
      {isLoading ? (
        <Loading />
      ) : !data?.plans.length ? (
        <Empty>Your accountant hasn't shared a tax plan yet.</Empty>
      ) : (
        <div className="space-y-8">
          {data.plans.map((p) =>
            p.plan ? (
              <PlanDocument key={p.id} p={p} />
            ) : p.questionnaire ? (
              <Questionnaire key={p.id} plan={p} questions={data.questions} />
            ) : (
              <Section key={p.id} title={p.title}>
                <p className="text-sm text-muted-foreground">Thank you. Your accountant is preparing this plan and will email you as soon as it's ready to read.</p>
              </Section>
            ),
          )}
        </div>
      )}
    </>
  );
}
